/**
 * First run: join a partner's log with an invite code, or name the babies
 * (with their birthdays, if the parent likes) and pick the unit bottles are
 * logged in (ounces or millilitres, suggested from the phone's locale) to
 * start a new log (two empty rows for twins, up to `MAX_BABIES`). Either way
 * ends on /welcome, which offers to connect an AI assistant before the first
 * tap.
 */
import {
  BOTTLE_UNIT_LABEL,
  BOTTLE_UNITS,
  type BottleUnit,
  earliestBirthDate,
  localCalendar,
  MAX_BABIES,
} from "@babia/checks";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "../components/ui/button.tsx";
import { Input, Label } from "../components/ui/input.tsx";
import { useToast } from "../components/ui/toast.tsx";
import {
  type NewBaby,
  phoneBottleUnit,
  phoneWeightUnit,
  useCreateBabies,
  useJoinHousehold,
  useUpdateHousehold,
} from "../lib/queries.ts";
import { cn } from "../lib/utils.ts";

/** A baby as typed. `dated` once the parent picks this row's birthday themselves. */
interface Row {
  name: string;
  birthDate: string;
  dated: boolean;
}

const emptyRow = (): Row => ({ name: "", birthDate: "", dated: false });

export function Setup() {
  const navigate = useNavigate();
  const toast = useToast();
  const createBabies = useCreateBabies();
  const household = useUpdateHousehold();
  const join = useJoinHousehold();
  const [rows, setRows] = useState<Row[]>([emptyRow(), emptyRow()]);
  const [unit, setUnit] = useState<BottleUnit>(phoneBottleUnit);
  /** The last birthday picked: what a row not yet picked shows. */
  const [shared, setShared] = useState("");
  const [code, setCode] = useState("");
  const today = localCalendar.isoDate(new Date());

  const cleaned: NewBaby[] = rows
    .map((r) => ({ name: r.name.trim(), ...(r.birthDate ? { birthDate: r.birthDate } : {}) }))
    .filter((b) => b.name);

  function setName(i: number, name: string) {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, name } : r)));
  }

  /** Twins share a birthday, so each one picked fills the rows not yet picked. */
  function setBirthDate(i: number, birthDate: string) {
    if (birthDate) setShared(birthDate);
    setRows((prev) =>
      prev.map((r, j) =>
        j === i
          ? { ...r, birthDate, dated: true }
          : r.dated || !birthDate
            ? r
            : { ...r, birthDate },
      ),
    );
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (cleaned.length === 0) return;
    createBabies.mutate(cleaned, {
      onSuccess: async () => {
        // A new household logs in ounces and reads pounds until told otherwise
        // (services/household.ts). Weights follow the phone's region, not the
        // bottle chip: a US family that measures millilitres still hears pounds.
        const weightUnit = phoneWeightUnit();
        if (unit !== "oz" || weightUnit !== "lb")
          await household
            .mutateAsync({
              ...(unit === "oz" ? {} : { bottleUnit: unit }),
              ...(weightUnit === "lb" ? {} : { weightUnit }),
            })
            .catch(() => {
              toast.show("Couldn't save the units. You can pick them in Settings.");
            });
        void navigate({ to: "/welcome", replace: true });
      },
      onError: (err) => toast.show(err.message),
    });
  }
  const saving = createBabies.isPending || household.isPending;

  function submitJoin(e: React.FormEvent) {
    e.preventDefault();
    join.mutate(code, {
      // Full reload: the session now points at the household, so every cache
      // and the selected baby must start over.
      onSuccess: () => window.location.assign("/welcome"),
      onError: (err) => toast.show(err.message),
    });
  }

  return (
    <div className="mx-auto max-w-[480px] pt-2">
      <h1 className="heading text-[26px] tracking-[-0.02em]">Babia</h1>
      <div className="head-rule" />

      <form onSubmit={submitJoin} className="mt-8 border border-divider bg-surface p-4">
        <h2 className="heading text-[24px]">Use an invite code</h2>
        <p className="mt-2 text-[14px] opacity-65">
          Partner already tracking your babies? Join their log here. No need to add them again.
        </p>
        <Label htmlFor="setup-join-code" className="mt-4">
          Invite code
        </Label>
        <Input
          id="setup-join-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="ABC234"
          autoComplete="one-time-code"
          autoCapitalize="characters"
          spellCheck={false}
          className="mt-1 uppercase tracking-[0.2em]"
        />
        <Button
          type="submit"
          variant="primary"
          className="mt-3 w-full"
          disabled={code.trim().length < 6 || join.isPending || saving}
        >
          {join.isPending ? "Joining…" : "Join partner's log"}
        </Button>
      </form>

      <form onSubmit={submit} className="mt-8">
        <p className="kicker mb-2">Or start a new log</p>
        <h2 className="heading text-[28px]">Who are we tracking?</h2>
        <p className="mt-1 text-[14px] opacity-65">
          First names as you'd say them at 3am, and birthdays if you like, so Babia can show each
          baby's age. You can change either later.
        </p>
        <div className="mt-5 flex flex-col gap-3">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2">
              <div className="min-w-0 flex-1">
                <Label htmlFor={`baby-${i}`}>Baby {i + 1}</Label>
                <Input
                  id={`baby-${i}`}
                  className="mt-1"
                  value={r.name}
                  autoComplete="off"
                  autoCapitalize="words"
                  maxLength={40}
                  placeholder={i === 0 ? "Auggie" : i === 1 ? "Griff" : ""}
                  onChange={(e) => setName(i, e.target.value)}
                />
              </div>
              <div className="w-[10.5rem] shrink-0">
                <Label htmlFor={`baby-${i}-born`}>Born</Label>
                <Input
                  id={`baby-${i}-born`}
                  type="date"
                  className="mt-1"
                  value={r.birthDate}
                  min={earliestBirthDate(today)}
                  max={today}
                  onChange={(e) => setBirthDate(i, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
        {rows.length < MAX_BABIES ? (
          <Button
            variant="ghost"
            className="mt-2 -ml-2"
            onClick={() => setRows((prev) => [...prev, { ...emptyRow(), birthDate: shared }])}
          >
            + Add another
          </Button>
        ) : null}
        <fieldset className="mt-5">
          <legend className="kicker">Bottles in</legend>
          <div role="group" aria-label="Bottle amounts" className="mt-1.5 flex gap-2">
            {BOTTLE_UNITS.map((u) => (
              <Button
                key={u}
                variant="chip"
                className={cn(unit === u && "is-selected")}
                aria-pressed={unit === u}
                onClick={() => setUnit(u)}
                disabled={saving}
              >
                {BOTTLE_UNIT_LABEL[u]}
              </Button>
            ))}
          </div>
          <p className="mt-1 text-[13px] opacity-65">
            For the whole family. You can change it later in Settings.
          </p>
        </fieldset>
        <Button
          type="submit"
          variant="secondary"
          className="mt-6 w-full"
          disabled={cleaned.length === 0 || saving || join.isPending}
        >
          {saving ? "Saving…" : "Start logging"}
        </Button>
      </form>
    </div>
  );
}
